const MOBILE_MEDIA_QUERY = "(max-width: 999px)";
const MOBILE_SIDEBAR_BASE_SELECTOR = "header-sidebar#sidebar-menu";
const MOBILE_SIDEBAR_SELECTOR = `${MOBILE_SIDEBAR_BASE_SELECTOR}[data-ig-navigation="mobile"]`;
const TRANSITION_TIMEOUT = 800;
const controllers = new Set();
const controllerBySidebar = new WeakMap();
const admittedSidebars = new WeakSet();
const suppressedSidebars = new WeakSet();
let canonicalMobileSidebar = null;
let isHeaderSidebarPrototypePatched = false;

function claimCanonicalSidebar(sidebar) {
  if (!canonicalMobileSidebar) {
    canonicalMobileSidebar = sidebar;
  }

  return canonicalMobileSidebar === sidebar;
}

function transferThemeEditorSidebar(sidebar) {
  if (
    !window.Shopify?.designMode ||
    !canonicalMobileSidebar ||
    canonicalMobileSidebar === sidebar ||
    !sidebar.matches(MOBILE_SIDEBAR_SELECTOR)
  ) {
    return;
  }

  const replacementSection = sidebar.closest(".shopify-section--header");
  const currentController = controllerBySidebar.get(canonicalMobileSidebar);

  if (!replacementSection || !currentController || replacementSection === currentController.section) {
    return;
  }

  const previousSidebar = canonicalMobileSidebar;
  currentController.destroy();

  if (previousSidebar.isConnected) {
    previousSidebar.remove();
  }

  if (suppressedSidebars.has(sidebar)) {
    suppressedSidebars.delete(sidebar);
    sidebar.inert = false;
    sidebar.removeAttribute("aria-hidden");
  }

  canonicalMobileSidebar = sidebar;
}

function isDuplicateMobileSidebar(sidebar) {
  return (
    sidebar instanceof Element &&
    sidebar.matches(MOBILE_SIDEBAR_BASE_SELECTOR) &&
    !claimCanonicalSidebar(sidebar)
  );
}

function quarantineDuplicateSidebar(sidebar) {
  suppressedSidebars.add(sidebar);
  sidebar.inert = true;
  sidebar.setAttribute("aria-hidden", "true");

  if (sidebar.hasAttribute("open")) {
    sidebar.removeAttribute("open");
  }

  sidebar.remove();
}

function installHeaderSidebarAdmissionGuard() {
  if (isHeaderSidebarPrototypePatched) {
    return;
  }

  const HeaderSidebar = window.customElements.get("header-sidebar");

  if (!HeaderSidebar) {
    return;
  }

  const prototype = HeaderSidebar.prototype;
  const connectedCallback = prototype.connectedCallback;
  const disconnectedCallback = prototype.disconnectedCallback;
  const attributeChangedCallback = prototype.attributeChangedCallback;

  prototype.connectedCallback = function () {
    transferThemeEditorSidebar(this);

    if (isDuplicateMobileSidebar(this)) {
      quarantineDuplicateSidebar(this);
      return;
    }

    const result = connectedCallback.call(this);
    admittedSidebars.add(this);
    return result;
  };

  prototype.disconnectedCallback = function () {
    if (suppressedSidebars.has(this) && !admittedSidebars.has(this)) {
      return;
    }

    admittedSidebars.delete(this);
    return disconnectedCallback.call(this);
  };

  prototype.attributeChangedCallback = function (name, oldValue, newValue) {
    transferThemeEditorSidebar(this);

    const isDetachedThemeEditorCandidate =
      window.Shopify?.designMode &&
      !this.isConnected &&
      this !== canonicalMobileSidebar &&
      name !== "open";
    const isUnidentifiedDuplicateOpening =
      name === "open" && canonicalMobileSidebar && this !== canonicalMobileSidebar;

    if (isDetachedThemeEditorCandidate) {
      return attributeChangedCallback.call(this, name, oldValue, newValue);
    }

    if (
      suppressedSidebars.has(this) ||
      isUnidentifiedDuplicateOpening ||
      isDuplicateMobileSidebar(this)
    ) {
      quarantineDuplicateSidebar(this);
      return;
    }

    return attributeChangedCallback.call(this, name, oldValue, newValue);
  };

  isHeaderSidebarPrototypePatched = true;
}

function containsMobileSidebar(node) {
  return (
    node instanceof Element &&
    (node.matches(MOBILE_SIDEBAR_SELECTOR) || node.querySelector(MOBILE_SIDEBAR_SELECTOR))
  );
}

function waitForEvent(target, eventName) {
  return new Promise((resolve) => {
    const handleEvent = () => {
      window.clearTimeout(timeout);
      resolve(true);
    };
    const timeout = window.setTimeout(() => {
      target.removeEventListener(eventName, handleEvent);
      resolve(false);
    }, TRANSITION_TIMEOUT);

    target.addEventListener(eventName, handleEvent, { once: true });
  });
}

function finishAnimationsWithin(root) {
  root.getAnimations({ subtree: true }).forEach((animation) => {
    animation.finished.catch(() => {});

    try {
      animation.finish();
    } catch {
      animation.cancel();
    }
  });
}

class MobileNavigationController {
  constructor(sidebar) {
    this.sidebar = sidebar;
    this.collapsiblePanel = sidebar.querySelector("header-sidebar-collapsible-panel");
    this.mainPanel = sidebar.querySelector(".header-sidebar__main-panel");
    this.parentButtons = Array.from(
      sidebar.querySelectorAll(".header-sidebar__main-panel .header-sidebar__linklist [aria-controls]"),
    );
    this.section = sidebar.closest(".shopify-section");
    this.abortController = new AbortController();
    this.mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.ownedAnimations = new Set();
    this.state = sidebar.open ? "root-open" : "closed";
    this.transitionVersion = 0;
    this.reopenRequested = false;
    this.singletonObserver = new MutationObserver((records) => {
      if (
        this.sidebar.isConnected &&
        records.some((record) => Array.from(record.addedNodes).some(containsMobileSidebar))
      ) {
        this.enforceSingleton();
      }
    });

    if (!this.collapsiblePanel || !this.mainPanel) {
      return;
    }

    this.singletonObserver.observe(document.documentElement, { childList: true, subtree: true });
    this.enforceSingleton();

    const listenerOptions = { signal: this.abortController.signal };
    const captureOptions = { capture: true, signal: this.abortController.signal };

    sidebar.addEventListener("dialog:before-show", () => this.handleBeforeShow(), listenerOptions);
    sidebar.addEventListener("dialog:after-show", () => this.handleAfterShow(), listenerOptions);
    sidebar.addEventListener("dialog:before-hide", () => this.handleBeforeHide(), listenerOptions);
    sidebar.addEventListener("dialog:after-hide", () => this.handleAfterHide(), listenerOptions);
    sidebar.addEventListener("click", (event) => this.handleSidebarClick(event), captureOptions);
    this.collapsiblePanel.addEventListener("click", (event) => this.handlePanelClick(event), captureOptions);
    document.addEventListener("click", (event) => this.handleDocumentClick(event), captureOptions);
  }

  get isMobile() {
    return this.mediaQuery.matches;
  }

  destroy() {
    this.transitionVersion += 1;
    this.cancelOwnedAnimations();
    this.singletonObserver.disconnect();
    this.abortController.abort();
    controllers.delete(this);
    controllerBySidebar.delete(this.sidebar);

    if (canonicalMobileSidebar === this.sidebar) {
      canonicalMobileSidebar = null;
    }
  }

  enforceSingleton() {
    document.querySelectorAll(MOBILE_SIDEBAR_SELECTOR).forEach((candidate) => {
      if (candidate !== this.sidebar) {
        quarantineDuplicateSidebar(candidate);
      }
    });
  }

  startTransition(state, transition) {
    const version = ++this.transitionVersion;

    this.state = state;
    this.cancelOwnedAnimations();
    this.transition = transition(version).catch(() => {});

    return this.transition;
  }

  isCurrentTransition(version) {
    return version === this.transitionVersion;
  }

  handleDocumentClick(event) {
    this.enforceSingleton();

    if (!this.isMobile || this.state !== "closing" || !(event.target instanceof Element)) {
      return;
    }

    const control = event.target.closest(`[aria-controls="${CSS.escape(this.sidebar.id)}"]`);

    if (!control || this.sidebar.contains(control)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.reopenRequested = true;
  }

  handleSidebarClick(event) {
    if (!this.isMobile || !(event.target instanceof Element)) {
      return;
    }

    const parentButton = event.target.closest(
      ".header-sidebar__main-panel .header-sidebar__linklist [aria-controls]",
    );

    if (!parentButton || !this.sidebar.contains(parentButton)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.state === "root-open") {
      this.openChildPanel(parentButton);
    }
  }

  handlePanelClick(event) {
    if (!this.isMobile || !(event.target instanceof Element)) {
      return;
    }

    const backButton = event.target.closest('[data-action="close-panel"]');

    if (!backButton) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.state === "child-open") {
      this.returnToRoot();
    }
  }

  handleBeforeShow() {
    if (!this.isMobile) {
      return;
    }

    this.transitionVersion += 1;
    this.reopenRequested = false;
    this.cancelOwnedAnimations();
    this.applyClosedState();
    this.state = "root-opening";
  }

  handleAfterShow() {
    if (!this.isMobile || this.state === "closing") {
      return;
    }

    this.state = "root-open";
  }

  handleBeforeHide() {
    if (!this.isMobile) {
      return;
    }

    this.transitionVersion += 1;
    this.state = "closing";
    this.cancelOwnedAnimations();
    this.applyClosedState();
  }

  handleAfterHide() {
    if (this.state !== "closing") {
      return;
    }

    this.applyClosedState();
    this.state = "closed";

    if (this.reopenRequested) {
      this.reopenRequested = false;
      window.queueMicrotask(() => this.sidebar.show());
    }
  }

  openChildPanel(parentButton) {
    const panelId = parentButton.getAttribute("aria-controls");
    const targetPanel = panelId ? this.collapsiblePanel.querySelector(`#${CSS.escape(panelId)}`) : null;

    if (!targetPanel || !this.sidebar.open) {
      return;
    }

    this.startTransition("child-opening", async (version) => {
      finishAnimationsWithin(this.mainPanel);

      const panelShown = this.collapsiblePanel.open
        ? Promise.resolve(true)
        : waitForEvent(this.collapsiblePanel, "dialog:after-show");
      const mainPanelHidden = this.animateMainPanelOut();

      this.parentButtons.forEach((button) => {
        button.setAttribute("aria-expanded", button === parentButton ? "true" : "false");
      });
      this.collapsiblePanel.setAttribute("aria-activedescendant", panelId);

      await Promise.all([panelShown, mainPanelHidden]);
      await Promise.resolve();

      if (!this.isCurrentTransition(version) || !this.sidebar.open) {
        return;
      }

      this.applyChildState(parentButton, targetPanel);
      this.state = "child-open";
    });
  }

  returnToRoot() {
    this.startTransition("root-opening", async (version) => {
      const activePanelId = this.collapsiblePanel.getAttribute("aria-activedescendant");
      const activeButton = this.parentButtons.find(
        (button) => button.getAttribute("aria-controls") === activePanelId,
      );

      finishAnimationsWithin(this.collapsiblePanel);

      const panelHidden = this.collapsiblePanel.open
        ? waitForEvent(this.collapsiblePanel, "dialog:after-hide")
        : Promise.resolve(true);

      this.collapsiblePanel.removeAttribute("aria-activedescendant");
      await panelHidden;
      await Promise.resolve();

      if (!this.isCurrentTransition(version) || !this.sidebar.open) {
        return;
      }

      this.applyRootState();
      activeButton?.focus({ preventScroll: true });
      await this.animateMainPanelIn();

      if (this.isCurrentTransition(version) && this.sidebar.open) {
        this.state = "root-open";
      }
    });
  }

  applyChildState(activeButton, activePanel) {
    this.parentButtons.forEach((button) => {
      button.setAttribute("aria-expanded", button === activeButton ? "true" : "false");
    });
    this.childPanels().forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });
    activePanel.hidden = false;
    this.mainPanel.setAttribute("aria-hidden", "true");
    this.mainPanel.inert = true;
    this.mainPanel.style.setProperty("opacity", "0");
    this.mainPanel.style.setProperty("transform", "translateX(-10px)");
  }

  applyRootState() {
    this.resetNestedState();
    this.collapsiblePanel.style.removeProperty("display");
    this.mainPanel.removeAttribute("aria-hidden");
    this.mainPanel.inert = false;
  }

  applyClosedState() {
    finishAnimationsWithin(this.sidebar);
    this.resetNestedState();
    finishAnimationsWithin(this.collapsiblePanel);
    this.clearAnimationStyles();
    this.collapsiblePanel.style.removeProperty("display");
    this.mainPanel.removeAttribute("aria-hidden");
    this.mainPanel.inert = false;
  }

  resetNestedState() {
    this.collapsiblePanel.removeAttribute("aria-activedescendant");

    if (this.collapsiblePanel.open) {
      this.collapsiblePanel.removeAttribute("open");
    }

    this.parentButtons.forEach((button) => button.setAttribute("aria-expanded", "false"));
    this.childPanels().forEach((panel) => {
      panel.hidden = true;
    });
    this.sidebar.querySelectorAll("details").forEach((details) => {
      details.open = false;
    });
    this.clearAnimationStyles();
  }

  childPanels() {
    return Array.from(this.collapsiblePanel.querySelectorAll(".header-sidebar__sub-panel"));
  }

  clearAnimationStyles() {
    const animatedElements = this.sidebar.querySelectorAll(
      ".header-sidebar__main-panel, .header-sidebar__main-panel li, .header-sidebar__footer, " +
        ".header-sidebar__collapsible-panel, .header-sidebar__sub-panel, " +
        ".header-sidebar__sub-panel li, .header-sidebar__back-button, .header-sidebar__promo",
    );

    animatedElements.forEach((element) => {
      element.style.removeProperty("opacity");
      element.style.removeProperty("transform");
    });
  }

  animateMainPanelOut() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return Promise.resolve();
    }

    return this.trackAnimation(
      this.mainPanel.animate(
        [
          { opacity: 1, transform: "translateX(0)" },
          { opacity: 0, transform: "translateX(-10px)" },
        ],
        { duration: 250, easing: "ease-out" },
      ),
    );
  }

  animateMainPanelIn() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return Promise.resolve();
    }

    return this.trackAnimation(
      this.mainPanel.animate(
        [
          { opacity: 0, transform: "translateX(-10px)" },
          { opacity: 1, transform: "translateX(0)" },
        ],
        { duration: 180, easing: "ease-out" },
      ),
    );
  }

  trackAnimation(animation) {
    this.ownedAnimations.add(animation);

    return animation.finished
      .catch(() => {})
      .finally(() => this.ownedAnimations.delete(animation));
  }

  cancelOwnedAnimations() {
    this.ownedAnimations.forEach((animation) => animation.cancel());
    this.ownedAnimations.clear();
  }
}

function attachSidebar(sidebar) {
  if (controllerBySidebar.has(sidebar)) {
    return;
  }

  if (!sidebar.matches(MOBILE_SIDEBAR_SELECTOR)) {
    return;
  }

  transferThemeEditorSidebar(sidebar);

  if (!claimCanonicalSidebar(sidebar)) {
    quarantineDuplicateSidebar(sidebar);
    return;
  }

  installHeaderSidebarAdmissionGuard();

  const controller = new MobileNavigationController(sidebar);

  if (!controller.collapsiblePanel || !controller.mainPanel) {
    return;
  }

  controllerBySidebar.set(sidebar, controller);
  controllers.add(controller);
}

function attachWithin(root = document) {
  if (root instanceof Element && root.matches(MOBILE_SIDEBAR_SELECTOR)) {
    attachSidebar(root);
  }

  root.querySelectorAll?.(MOBILE_SIDEBAR_SELECTOR).forEach(attachSidebar);
}

attachWithin();

document.addEventListener("shopify:section:load", (event) => attachWithin(event.target));
document.addEventListener("shopify:section:unload", (event) => {
  controllers.forEach((controller) => {
    if (controller.section === event.target || event.target.contains(controller.sidebar)) {
      controller.destroy();
    }
  });
});
